import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePalletStore } from "@/store/pallets";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import PreShipLean from "./SummaryLean";
import PostShipLean from "./post-ship-lean";
import ShipWalkTest from "./ship-test-walk";
import StackTestWeekly from "./stack-test-weekly";
import PreShipForceLoad from "./pre-ship-force-load";
import UnitLoadHeight from "./until-load-height";
import PrimaryPakageDamageRQL from "./primary-pakage-damage-RQL";
import Footer from "../footer";
import LeanConsolidatedView from "../common-components/LeanConsolidatedView";
import SummaryLean from "./SummaryLean";
import SummaryFtl from "./SummaryFtl";
import SummaryStackUnitLoadHeight from "./SummaryStackUnitLoadHeight";
import { showSuccessToast, showErrorToast } from "@/components/toast/toast";
import { Skeleton } from "@/components/ui/skeleton";
import UnitLoadHeightConsolidatedView from "../post-stack/stack-unit-load-height";
import PostStackLeanSummary from "./PostStackLeanSummary";
// Skeleton for the summary table
const SummaryTableSkeleton = () => (
  <div className="w-full space-y-6">
    {/* Table header skeleton */}
    <div className="grid grid-cols-6 gap-2">
      {Array(6).fill(0).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>

    {/* Table rows skeleton */}
    {Array(5).fill(0).map((_, rowIndex) => (
      <div key={rowIndex} className="grid grid-cols-6 gap-2">
        {Array(6).fill(0).map((_, colIndex) => (
          <Skeleton key={colIndex} className="h-8 w-full" />
        ))}
      </div>
    ))}
  </div>
);

// Skeleton for a full tab content
const TabContentSkeleton = () => (
  <div className="space-y-6 p-8">
    <Skeleton className="h-6 w-24" />
    <div className="w-full flex-center justify-between mb-6">
      <Skeleton className="h-10 w-40" />
    </div>
    <SummaryTableSkeleton />
  </div>
);

interface SummaryProps {
  projectId?: string;
  isOffline?: boolean;
  onOfflineSubmit?: (values: any) => Promise<void>;
}

export default function Summary({
  projectId,
  isOffline,
  onOfflineSubmit
}: SummaryProps) {
  const { palletTypes, activePallet } = usePalletStore((state) => state);
  const [isLoading, setIsLoading] = useState(true);

  // Simulate loading state for demo - remove this in production
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const paths = [
    {
      id: 1,
      tabName: "Pre-Ship Lean",
      tabContent: <SummaryLean variant="preShip" />,
    },

    {
      id: 2,
      tabName: "Post-Ship Lean",
      tabContent: <SummaryLean variant="postShip" />,
    },
    { id: 3, tabName: "Position (Walk)", tabContent: <ShipWalkTest variant="postShip" /> },
 

    {
      id: 4,
      tabName: "Pre-Ship FTL",
      tabContent: <SummaryFtl variant="preShip" />,
    },
    {
      id: 5,
      tabName: "Post-Ship FTL",
      tabContent: <SummaryFtl variant="postShip" />,
    },
    {
      id: 6,
      tabName: "Stack Lean Weekly",
      tabContent: <SummaryLean variant="postStack" isSum={false} />,
    },
    // {
    //   id: 7,
    //   tabName: "Stack UnitLoad Height",
    //   tabContent: < variant="postStack" isSum={true} />,
    // },
    { id: 7, tabName: "Stack Height Compression", tabContent: <SummaryStackUnitLoadHeight isSum={false} variant="postStack" activeWeek="Post stack unit load height (with load still on)"/> },
    { id: 8, tabName: "Stack Height Rebound", tabContent: <SummaryStackUnitLoadHeight isSum={false} variant="postStack" activeWeek="Post stack unit load with Rebound (30 min post load removal)"/> },
    // {
    //   id: 8,
    //   tabName: "Primary Package Damage - RQL",
    //   tabContent: <PrimaryPakageDamageRQL />,
    // },
  ];

  const handleSubmit = async () => {
    // if (isOffline && onOfflineSubmit) {
    //   await onOfflineSubmit(values);
    //   return;
    // }

    try {
      // Existing online submission logic
      // await submitSummaryData(values);
      showSuccessToast("Summary data saved successfully");
    } catch (error) {
      console.error(error);
      showErrorToast("Failed to save summary data");
    }
  };

  if (isLoading) {
    return (
      <div className="w-full flex h-[calc(100vh-140px)]">
        <div className="w-[170px] h-full bg-light-blue">
          <div className="flex flex-col py-8 space-y-2">
            {Array(6).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-10 w-[140px] mx-auto" />
            ))}
          </div>
        </div>
        <div className="flex-1">
          <TabContentSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex h-[calc(100vh-140px)]">
      <Tabs defaultValue="Pre-Ship Lean" className="w-full flex">
        <TabsList className="w-[170px] h-full bg-light-blue flex flex-col justify-start py-8">
          {paths.map((path) => (
            <TabsTrigger
              key={path.id}
              value={path.tabName}
              className="justify-start px-4 py-3 text-left"
            >
              {path.tabName}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="flex-1 overflow-hidden">
          {paths.map((path) => (
            <TabsContent
              key={path.id}
              value={path.tabName}
              className={cn(
                "h-full overflow-y-auto p-8 pb-[100px]",
                path.tabName !== "Ship Test Truck Layout" && "space-y-4"
              )}
            >
              {path.tabName !== "Stack Test Layout" && (
                <>
                  <p className="semi-bold-14">Summary</p>
                  <div className="w-full flex-center justify-between">
                    <>
                      {/* {palletTypes.map((item:any, i:number) => {
                        return (
                          <Button
                            className={cn(
                              "bg-grey/10 text-placeholder",
                              (activePallet === item ||
                                palletTypes.length === 1) &&
                                "bg-blue text-white py-1 h-8 hover:bg-white hover:text-blue border-2 border-blue"
                            )}
                            key={i}
                          >
                            {item}
                          </Button>
                        );
                      })} */}
                    </>
                  </div>
                </>
              )}
              {path.tabContent}
            </TabsContent>
          ))}
        </div>
      </Tabs>
      <Footer layout onSubmit={handleSubmit} />
    </div>
  );
}
